import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { checkAdmin, getPassCode, checkApiError } from '../../utils';
import { Button, Table, Form, Dropdown, Modal } from 'react-bootstrap';
import './../../css/manageuserevent.css';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';

const UserManagement = ({showLoading}) => {
    const [attendees, setAttendees] = useState([]);
    const [selectedAttendee, setSelectedAttendee] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [attendeesPerPage, setAttendeesPerPage] = useState(5);
    const [searchTerm, setSearchTerm] = useState('');
    const isAdmin = checkAdmin();
    const [userTypeFilter, setUserTypeFilter] = useState("All");
    
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const navigate = useNavigate();

    const [sortConfig, setSortConfig] = useState({ key: 'organization', direction: 'asc' });

    useEffect(() => {
        if(getPassCode() === ""){
            navigate("/login");
        }
        const fetchAttendees = async () => {
            try {
                showLoading(true);
                const response = await fetch(`${process.env.REACT_APP_API_URL}/Attendee/getAll`, {
                    method: 'get',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${getPassCode()}`
                    }
                });

                if(checkApiError(response.status)){ navigate("/login"); return; }
            
                if (response.status === 200) {
                    const data = await response.json();
                    setAttendees(data.data);
                } else {
                    setAttendees([]);
                }
            } catch (error) {
                console.log(error);
            } finally{
                showLoading(false);
            }
        };

        if (isAdmin) {
            fetchAttendees();
        }

    }, [isAdmin, showLoading, navigate]);

    const handleUserTypeFilter = (_ut) =>{
        setCurrentPage(1);
        setSelectedAttendee(null);        
        setUserTypeFilter(_ut);
    }

    const filteredAttendees = attendees.filter((attendee) => {
        // 1. user type Filtering
        if (userTypeFilter.toLowerCase() === 'all') {
            return true;
        } else if (userTypeFilter.toLowerCase() === 'encora') {
            return attendee.userType && attendee.userType.toLowerCase() === 'encora';
        } else if (userTypeFilter.toLowerCase() === 'cab') {
            return !attendee.userType || attendee.userType.toLowerCase() !== 'encora';
        }    
        return false;
    
    }).filter(attendee => {
            // 2. Apply search term filtering AFTER user type filtering
            // return Object.values(attendee).some(value =>
            // value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
            const fullName = `${attendee.firstName || ''} ${attendee.lastName || ''}`.trim().toLowerCase();
            const searchTermLower = searchTerm.toLowerCase();
            if (fullName.includes(searchTermLower)) {
                return true;
            }else{
                return (
                    (attendee.firstName && attendee.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (attendee.lastName && attendee.lastName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (attendee.organization && attendee.organization.toLowerCase().includes(searchTerm.toLowerCase()))
                );
            }
    });

    useEffect(() => { setSelectedAttendee(null); }, [sortConfig]);

    useEffect(() => { setSelectedAttendee(null); }, [userTypeFilter]);
    
    useEffect(() => { setSelectedAttendee(null); }, [searchTerm]);

    useEffect(() => { setSelectedAttendee(null); }, [currentPage]);
    
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
        setSelectedAttendee(null); // Reset selection on sort
    };

    const sortedAttendees = [...filteredAttendees].sort((a, b) => {
        if (sortConfig.key === null) {
            return 0; // No sorting
        }

        const valueA = a[sortConfig.key] ? a[sortConfig.key].toString().toLowerCase() : ""; 
        const valueB = b[sortConfig.key] ? b[sortConfig.key].toString().toLowerCase() : ""; 
    
        if (valueA < valueB) {
            return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (valueA > valueB) {
            return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
    });

    const totalPages = Math.ceil(filteredAttendees.length / attendeesPerPage);

    // Pagination logic
    const indexOfLastUser = currentPage * attendeesPerPage;
    const indexOfFirstUser = indexOfLastUser - attendeesPerPage;

    // --- Updated to use the filtered attendees ---
    const currentAttendees = sortedAttendees.slice(indexOfFirstUser, indexOfLastUser);

    const paginate = (pageNumber) => { setCurrentPage(pageNumber); setSelectedAttendee(null); };

    const handleAddEditAttendee = (attendee, mode) => {
        attendee = attendee || false;
        mode === 'add' ? navigate('/user-management/attendee', {state: {attendee, mode}}) : navigate('/user-management/attendee', {state: {attendee, mode}});
    };

    const handleDeleteAttendee = async () => {
        setShowDeleteModal(true); 
    };

    const handleConfirmDelete = async () => {
        const userId = selectedAttendee.userId;
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Attendee/Delete?userId=${userId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${getPassCode()}`,
                    'Accept': "*/*",
                    'Content-Type': 'application/json'
                }
            });

            if(checkApiError(response.status)){ navigate("/login"); return; }

            if (response.status === 200) {
                toast.success('Member record deleted.');
            }else{
                toast.error('Error!');
            }

            setAttendees(attendees.filter((user) => user.userId !== userId));
            setSelectedAttendee(null);
          } catch (error) {
            toast.error('Error!');
          } finally{
            setShowDeleteModal(false);
          }
    };

    const handleCloseDeleteModal = () => setShowDeleteModal(false);

    const handleAttendeeSelection = (attendeeId) => {
        setSelectedAttendee(attendees.find(attendee => attendee.userId === attendeeId));
    }

    const handlePaginateShowCount = (e) => {
        const countValue = parseInt(Number(e.target.value).toString());
        if(e.target.value === '' || parseInt(e.target.value) === 0){
            setAttendeesPerPage(0);
            setCurrentPage(0);
            return;
        }
        setCurrentPage(1);
        e.target.value = countValue;
        const value = countValue >= attendees.length ? attendees.length : countValue;
        value < 0 ? setAttendeesPerPage(0) : setAttendeesPerPage(value);
    }

    const handleCurrentPageCount = (e) => {
        if(e.target.value === '' || parseInt(e.target.value) === 0){
            setCurrentPage(1);
        }else if(parseInt(e.target.value) > totalPages){
            setCurrentPage(totalPages);
        }else{
            setCurrentPage(parseInt(e.target.value));
        }
    }

    const debouncedSearch = debounce((term) => {
        setSearchTerm(term);
        setCurrentPage(1); // Reset to first page on new search
    }, 300);

    const handleSearchChange = (e) => {
        debouncedSearch(e.target.value);
    };

    /* const handlePerPageAttendeeCount = ()=>{
        if(attendeesPerPage > attendees.length){
            setAttendeesPerPage(attendees.length);
        }
        return attendeesPerPage;
    } */

    const renderCustomPaginationItems = () => {
        let items = [];
        //previous
        items.push(
            <button type='button' className='pg-btn pg-prev-btn' disabled={currentPage === 1} onClick={() => paginate(currentPage - 1)}>&nbsp;&nbsp;</button>
        );
        // Always show first page
        items.push(
            <button type='button' className='pg-btn' disabled={currentPage === 1} onClick={() => paginate(1)}>1</button>
        );
        // Always show the second page
        if(totalPages > 1 && currentPage < 3) {
          items.push(
              <button type='button' className='pg-btn' disabled={currentPage === 2} onClick={() => paginate(2)}>2</button>
          );
        }

        // Always show the current page.  Put a conditional around this to prevent duplicates.
        if(currentPage !== 1 && currentPage !== 2 && currentPage !== totalPages) {
            items.push(
                <button type='button' className='pg-btn' disabled={currentPage !== totalPages} onClick={() => paginate(currentPage)}>{currentPage}</button>
            )
        }

        // Show ellipsis if there are more pages between 2 and last
        if (totalPages > 2) {
            items.push(
                <button type='button' className='pg-btn pg-eclipses-btn' disabled>&#8230;</button>
            );
        } 

        // Always show last page if there is more than one page
        if(totalPages > 1 && (items.length-1 !== totalPages)) {
          items.push(
              <button type='button' className='pg-btn' disabled={currentPage === totalPages} onClick={() => paginate(totalPages)}>{totalPages}</button> 
          )
        }        
        // Next
        items.push(
            <button type='button' className='pg-btn pg-next-btn' disabled={currentPage === totalPages} onClick={() => paginate(currentPage + 1)}>&nbsp;&nbsp;</button>
        );
        
        return items;
    };
    
    const getuniqueType = (attendees) => {
        /* const organizations = new Set();
        attendees.forEach(attendee => {
            if (attendee.organization) {
                organizations.add(attendee.organization);
            }
        });        
        return Array.from(organizations).sort(); */
        const usertypes = [
            {type: 'Encora', text: 'Encora'}, 
            {type: 'Cab', text: 'Cab'}
        ];
        return [...new Set(usertypes)]; 
    };
    
    const uniqueType = getuniqueType(attendees);

    const showOutOfEntries = () => {
        const startEntry = (attendeesPerPage === 0 || filteredAttendees.length === 0) ? 0 : (currentPage - 1) * attendeesPerPage + 1;
        const endEntry = Math.min(currentPage * attendeesPerPage, filteredAttendees.length);
        
        return (
            <>
            Showing <span className='color-black roboto-bold'>{startEntry}-{endEntry}</span> out of <span className='color-black roboto-bold'>{filteredAttendees.length}</span> Entries
            </>
        );
    };

    if (!isAdmin) {
        return <div>You do not have permission to access this page.</div>;
    }

    return (
        <div className="manage-user-events w3-container mt-4 mb-4">
            <p className="roboto-bold mb-2 color-black t-h">Manage Members</p>

            <nav aria-label="breadcrumb">
                <ol className="breadcrumb fs-0-8">
                    <li className="breadcrumb-item">
                        <Link className='color-purple' to="/dashboard">Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                        Manage Members
                    </li>
                </ol>
            </nav>

            <div className="row mt-4">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body p-0">
                            <div className='d-flex justify-content-between align-items-md-center align-items-start p-4 pt-3 pb-3 flex-column flex-md-row'>
                                <p className='fw-500 m-md-0 s-h mb-3'>Member List</p>
                                <div className='action-btn-container'>                                    
                                    <div className="d-flex justify-content-start justify-content-md-end align-items-center color-gray flex-wrap">
                                        <div className='search-container color-gray d-flex align-items-center mb-2 mb-md-0 me-3'>
                                            <label htmlFor="userTypeFilter" className="me-2">Search by:</label>
                                            <Form.Group controlId="search" className="d-flex align-items-center">
                                                <Form.Control type="text" placeholder="Name or Organization" onChange={handleSearchChange} />
                                            </Form.Group>
                                        </div>
                                        <div className='d-flex mb-2 mb-md-0 align-items-center'>
                                            <label htmlFor="userTypeFilter" className="me-2">User Type:</label>
                                            <Dropdown className="me-3" style={{minWidth: '80px'}}>
                                                <Dropdown.Toggle variant="outline-secondary w-100 text-start" id="organizationDropdown">
                                                    {userTypeFilter}
                                                </Dropdown.Toggle>

                                                <Dropdown.Menu>
                                                    <Dropdown.Item onClick={() => handleUserTypeFilter("All")}>All</Dropdown.Item>
                                                    {uniqueType.map((user, index) => (
                                                    <Dropdown.Item key={'key_'+index} onClick={() => handleUserTypeFilter(user.type)}>
                                                        {user.text}
                                                    </Dropdown.Item>
                                                    ))}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>
                                        <div className='d-flex mb-2 mb-md-0 align-items-center text-nowrap'>
                                        <Button variant="purple" onClick={() => handleAddEditAttendee(null, 'add')} className="me-3">Add Member</Button>

                                        <Button id="btnDelete" variant="danger" disabled={selectedAttendee === null} onClick={() => { handleDeleteAttendee() }}>Delete</Button>

                                        <div className='d-flex justify-content-center align-items-center ms-2' style={{ width: "140px" }}>
                                            Show <input className='form-control ms-2 me-2 text-center input-sm' type="number" value={attendeesPerPage} onChange={handlePaginateShowCount} style={{width: "45px", padding:"0.4rem 0.75rem"}} /> Entries
                                        </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <Table responsive>
                                <thead className="table-light">
                                    <tr>
                                        <th scope="col" className='mw-60' style={{width: "60px"}}></th>
                                        <th scope="col" className='mw-120'>First Name</th>
                                        <th scope="col" className='mw-120'>Last Name</th>
                                        <th scope="col" className='mw-140'>Designation</th>
                                        <th scope="col" className='mw-120' onClick={() => requestSort('organization')} style={{ cursor: 'pointer' }}>
                                            Organization
                                            {sortConfig.key === 'organization' && (
                                                <span className={`positiion-relative ms-1 icons icon-sort ${sortConfig.direction === 'asc' ? 'icon-sort-asc' : 'icon-sort-desc'}`}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-expand position-absolute" viewBox="0 0 16 16">
                                                        <path d="M3.646 9.146a.5.5 0 0 1 .708 0L8 12.793l3.646-3.647a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 0-.708m0-2.292a.5.5 0 0 0 .708 0L8 3.207l3.646 3.647a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 0 0 0 .708"/>
                                                    </svg>
                                                </span>
                                            )}
                                        </th>
                                        <th scope="col" className='mw-70 text-center' style={{width: "80px"}}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentAttendees.length === 0 ?
                                     (
                                        <tr>
                                            <td colSpan="6" className="text-center">No attendees found.</td>
                                        </tr>
                                    ) :
                                     currentAttendees.map((attendee, index) => (
                                        <tr key={'aKey_'+index}>
                                            <td className='text-center'>
                                                <input type="radio" name="attendee" className="btn-radio icons icon-radio" checked={selectedAttendee && selectedAttendee.userId === attendee.userId} onChange={()=>handleAttendeeSelection(attendee.userId)} />
                                            </td>
                                            <td>{attendee.firstName}</td>
                                            <td>{attendee.lastName}</td>
                                            <td>{attendee.designation}</td>
                                            <td>{attendee.organization}</td>
                                            <td className='text-center'>
                                                <Button variant="" size="sm" onClick={() => handleAddEditAttendee(attendee, 'edit')} className='icons icon-edit-solid p-0'>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-pencil-square" viewBox="0 0 16 16">
                                                        <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                                        <path d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"/>
                                                    </svg>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            {/* Pagination for the filtered attendees */}
                            <div className="d-flex justify-content-between align-items-sm-center align-items-start ps-4 pe-4 mb-3 mt-1 flex-column flex-sm-row color-gray">
                                <div className='m-sm-0 mb-3'>{showOutOfEntries()}</div>
                                <div className='d-flex align-items-center'>
                                    { false && 
                                        <div className='d-flex justify-content-center align-items-center hide'>
                                            Page <input className='form-control ms-2 me-2 text-center input-sm' type="number" disabled={attendeesPerPage >= attendees.length} value={currentPage} onChange={handleCurrentPageCount} style={{width: "45px", padding:"0.4rem 0.75rem"}} /> Entries
                                        </div>
                                    }
                                    {attendeesPerPage > 0 &&
                                        <div className="custom-paginaton ms-3">
                                            {renderCustomPaginationItems()}
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Do you want to remove the "<span className='fw-bold'>{selectedAttendee && (selectedAttendee?.firstName + ' ' + selectedAttendee.lastName)}</span>" member record?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseDeleteModal}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default UserManagement;

